/**
 * BrandLogo — official Circularity Global brand assets, theme-aware.
 *
 * Variants:
 *   "symbol"   — the orbit/swoosh mark only.
 *                 • Default: CSS-masked from a local PNG (theme-tinted, hover-lime).
 *                 • `official` prop swaps in the full-colour official square PNG
 *                   (white-on-transparent in dark mode, black-on-transparent in light mode).
 *   "wordmark" — the "circularity" wordmark (theme-swapped PNG).
 *   "lockup"   — wordmark only (default; "Global" suffix removed).
 *
 * The default symbol uses CSS mask-image so it inherits its colour from
 * `currentColor` or any explicit `color` prop — looks right in any theme
 * automatically. Use `official` when you need the full brand artwork
 * (press kit, large hero badges, partner co-branding).
 */
const ASSETS = {
  // Wordmarks (cropped, transparent background)
  wordmarkWhite: "https://customer-assets.emergentagent.com/job_circular-os/artifacts/nbw359w4_Circularity%20-%20Logo_White_Cropped_NoBG.png",
  wordmarkBlack: "https://customer-assets.emergentagent.com/job_circular-os/artifacts/gj1zo1rd_Circularity%20-%20Logo_Black_Cropped_NoBG.png",
  // Official square symbol marks (full artwork, transparent background)
  symbolWhiteSquare: "https://customer-assets.emergentagent.com/job_circular-os/artifacts/r2c08ip3_Circularity%20-%20Logo_White_Square_NoBG.png",
  symbolBlackSquare: "https://customer-assets.emergentagent.com/job_circular-os/artifacts/sl40e8jt_Circularity%20-%20Logo_Black_Square_NoBG.png",
  // Arrows-only symbol mark (no wordmark) — used for the centre of the
  // flywheel. White arrows + dotted circle on a black backdrop. Inverted
  // via CSS filter in light mode (see .csos-fly-center-symbol).
  symbolArrowsOnly: "https://customer-assets.emergentagent.com/job_circular-os/artifacts/vgc9p9k9_image.png",
  // Local CSS-mask source — fast monochrome symbol for nav/footer use
  // (identical silhouette to the official square marks above).
};

export function BrandSymbol({
  size = 28,
  color,
  className = "",
  style = {},
  testId = "brand-symbol",
  spin = true,
  official = false,
}) {
  if (official) {
    return (
      <span
        data-testid={testId}
        role="img"
        aria-label="Circularity Global mark"
        className={`relative inline-block flex-shrink-0 ${spin ? "brand-symbol-spin" : ""} ${className}`}
        style={{ width: size, height: size, ...style }}
      >
        <img
          src={ASSETS.symbolWhiteSquare}
          alt=""
          className="absolute inset-0 w-full h-full object-contain hidden dark:block"
          loading="eager"
          decoding="async"
        />
        <img
          src={ASSETS.symbolBlackSquare}
          alt=""
          className="absolute inset-0 w-full h-full object-contain block dark:hidden"
          loading="eager"
          decoding="async"
        />
      </span>
    );
  }

  return (
    <span
      data-testid={testId}
      role="img"
      aria-label="Circularity Global mark"
      className={`inline-block flex-shrink-0 brand-symbol ${spin ? "brand-symbol-spin" : ""} ${className}`}
      style={{
        width: size,
        height: size,
        ...(color ? { color } : null),
        ...style,
      }}
    />
  );
}

export default function BrandLogo({
  variant = "lockup",   // "symbol" | "wordmark" | "lockup"
  size = 28,
  className = "",
  testId = "brand-logo",
  color,                // optional override for symbol colour
  spin = true,          // set false to disable rotation (rare)
  official = false,     // symbol-only: render full-colour official PNG instead of CSS mask
}) {
  if (variant === "symbol") {
    return <BrandSymbol size={size} color={color} className={className} testId={testId} spin={spin} official={official} />;
  }

  const wordmarkStyle = { height: Math.round(size * 0.78) };

  const Wordmark = (
    <span className="relative inline-flex items-center" style={wordmarkStyle} aria-hidden>
      <img
        src={ASSETS.wordmarkWhite}
        alt=""
        className="h-full w-auto object-contain hidden dark:block"
        loading="eager"
        decoding="async"
      />
      <img
        src={ASSETS.wordmarkBlack}
        alt=""
        className="h-full w-auto object-contain block dark:hidden"
        loading="eager"
        decoding="async"
      />
    </span>
  );

  // wordmark | lockup — both currently render just the wordmark
  return (
    <span className={`inline-flex items-center ${className}`} data-testid={testId} role="img" aria-label="Circularity Global">
      {Wordmark}
    </span>
  );
}

// Re-export asset URLs for callers that need raw assets (favicon, OG images,
// press kit, partner co-branding, social cards).
BrandLogo.assets = ASSETS;
